import { useEffect, useState, useCallback } from "react";
import { FileSpreadsheet, FileText, Lock } from "lucide-react";
import { api, API } from "@/lib/api";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const COST_LABELS = { talent: "Talentos", viatic: "Viáticos", production: "Producción", logistics: "Logística", equipment: "Equipos", other: "Otros" };
const PERIOD_PRESETS = [
  { value: "month", label: "Mes específico" },
  { value: "1m", label: "Último mes (30d)" },
  { value: "3m", label: "Últimos 3 meses" },
  { value: "6m", label: "Últimos 6 meses" },
  { value: "12m", label: "Últimos 12 meses" },
  { value: "custom", label: "Rango personalizado" },
];

export default function MonthlyReportsPage() {
  const today = new Date();
  const [periodKind, setPeriodKind] = useState("month");
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currency, setCurrency] = useState("BOB");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildQuery = useCallback(() => {
    const qs = new URLSearchParams({ currency });
    if (periodKind === "month") {
      qs.set("year", String(year));
      qs.set("month", String(month));
    } else if (periodKind === "custom") {
      if (startDate) qs.set("start_date", startDate);
      if (endDate) qs.set("end_date", endDate);
    } else {
      const n = periodKind.replace("m", "");
      qs.set("months_back", n);
    }
    return qs.toString();
  }, [periodKind, year, month, startDate, endDate, currency]);

  const load = async () => {
    setLoading(true);
    try { setData(await api.get(`/admin/com/reports/monthly?${buildQuery()}`)); }
    catch (e) { setData({ summary: {}, rows: [] }); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [periodKind, year, month, startDate, endDate, currency]);

  const download = async (kind) => {
    const ext = kind === "xlsx" ? "xlsx" : "pdf";
    const token = localStorage.getItem("influcas_token");
    const r = await fetch(`${API}/admin/com/reports/monthly.${ext}?${buildQuery()}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { alert("Error al generar el archivo"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `reporte-${(s.period || "periodo").replace(/[^\w]/g, "-")}.${ext}`; a.click(); URL.revokeObjectURL(url);
  };

  const s = data?.summary || {};
  const rows = data?.rows || [];

  return (
    <div data-testid="commercial-reports">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1 flex items-center gap-3">Reportes contables <Lock className="w-5 h-5 text-amber-500" /></h1>
          <p className="text-sm text-muted-foreground">Cierres del período, costos, comisiones y ganancia neta · USO INTERNO ÚNICAMENTE</p>
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Período</label>
            <select value={periodKind} onChange={(e) => setPeriodKind(e.target.value)} className="h-10 px-3 bg-background border border-border rounded-sm text-sm" data-testid="report-period">
              {PERIOD_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {periodKind === "month" && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Año</label>
                <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-10 px-3 bg-background border border-border rounded-sm text-sm">
                  {[2024, 2025, 2026, 2027, 2028].map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Mes</label>
                <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="h-10 px-3 bg-background border border-border rounded-sm text-sm" data-testid="report-month">
                  {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{`${String(i + 1).padStart(2, "0")} · ${m}`}</option>)}
                </select>
              </div>
            </>
          )}
          {periodKind === "custom" && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Desde</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-10 px-3 bg-background border border-border rounded-sm text-sm" data-testid="report-start" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Hasta</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-10 px-3 bg-background border border-border rounded-sm text-sm" data-testid="report-end" />
              </div>
            </>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Moneda</label>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="h-10 px-3 bg-background border border-border rounded-sm text-sm" data-testid="report-currency">
              <option value="BOB">BOB · Boliviano</option>
              <option value="USD">USD · Dólar</option>
            </select>
          </div>
          <button onClick={() => download("xlsx")} data-testid="report-xlsx" className="inline-flex items-center gap-2 px-4 h-10 bg-emerald-600 text-white rounded-sm font-semibold text-sm hover:bg-emerald-700"><FileSpreadsheet className="w-4 h-4" /> Excel</button>
          <button onClick={() => download("pdf")} data-testid="report-pdf" className="inline-flex items-center gap-2 px-4 h-10 bg-primary text-primary-foreground rounded-sm font-semibold text-sm"><FileText className="w-4 h-4" /> PDF</button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-3">Período activo: <span className="font-semibold">{s.period || "—"}</span> {s.range_from && <>· <span>{s.range_from} → {s.range_to}</span></>}</p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <KPI label="Cierres" value={s.deals_count || 0} />
        <KPI label="Ingresos" value={`${currency} ${(s.revenue_total || 0).toLocaleString()}`} />
        <KPI label="Costos" value={`${currency} ${(s.internal_costs_total || 0).toLocaleString()}`} subtle />
        <KPI label="Com. Agencia" value={`${currency} ${(s.agency_commission_total || 0).toLocaleString()}`} subtle />
        <KPI label="Com. Ejecutivo" value={`${currency} ${(s.executive_commission_total || 0).toLocaleString()}`} subtle />
        <KPI label="Ganancia neta" value={`${currency} ${(s.net_profit_total || 0).toLocaleString()}`} highlight extra={`${(s.margin_pct || 0).toFixed(2)}% margen`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        <div className="lg:col-span-2 border border-border rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Cierre</th>
                <th className="text-left p-3">Propuesta</th>
                <th className="text-left p-3">Cliente</th>
                <th className="text-right p-3">Bruto</th>
                <th className="text-right p-3">Costos</th>
                <th className="text-right p-3">C. Ag.</th>
                <th className="text-right p-3">C. Ej.</th>
                <th className="text-right p-3">Neto</th>
                <th className="text-right p-3">Margen</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">Cargando...</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">Sin cierres en este período/moneda.</td></tr>}
              {rows.map((r) => (
                <tr key={r.proposal_id} className="border-t border-border hover:bg-secondary/30">
                  <td className="p-3 text-xs">{(r.closed_at || r.ref_date || "").slice(0, 10)}</td>
                  <td className="p-3 font-semibold">{r.title}</td>
                  <td className="p-3">{r.client || "—"}</td>
                  <td className="p-3 text-right">{r.gross_price.toLocaleString()}</td>
                  <td className="p-3 text-right text-amber-500">{r.internal_costs_total.toLocaleString()}</td>
                  <td className="p-3 text-right text-amber-500/80">{(r.agency_commission || 0).toLocaleString()}</td>
                  <td className="p-3 text-right text-amber-500/80">{(r.executive_commission || 0).toLocaleString()}</td>
                  <td className={`p-3 text-right font-bold ${r.net_profit >= 0 ? "text-emerald-500" : "text-destructive"}`}>{r.net_profit.toLocaleString()}</td>
                  <td className="p-3 text-right text-xs">{r.margin_pct.toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border border-border rounded-sm p-5">
          <h3 className="font-display font-bold mb-3">Desglose de costos</h3>
          {Object.keys(s.costs_by_type || {}).length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin costos registrados.</p>
          ) : (
            <ul className="space-y-2">
              {Object.entries(s.costs_by_type || {}).map(([k, v]) => (
                <li key={k} className="flex justify-between border-b border-border pb-2">
                  <span className="text-sm">{COST_LABELS[k] || k}</span>
                  <span className="font-bold text-sm">{currency} {v.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground italic"><Lock className="w-3 h-3 inline mr-1 text-amber-500" /> Confidencial · No compartir con clientes ni equipo de producción.</p>
    </div>
  );
}

function KPI({ label, value, highlight, subtle, extra }) {
  return (
    <div className={`border rounded-sm p-4 ${highlight ? "border-primary bg-primary/5" : subtle ? "border-amber-500/40" : "border-border"}`}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{label}</p>
      <p className={`font-display font-black text-xl tracking-tighter ${highlight ? "text-primary" : subtle ? "text-amber-500" : ""}`}>{value}</p>
      {extra && <p className="text-[11px] text-muted-foreground mt-1">{extra}</p>}
    </div>
  );
}
